/**
 * Fractional index keys (`docs/spec/collections.md` §2.6): strings over a
 * 62-character ASCII-ordered alphabet compared lexicographically, so the order
 * a client computes is the order the database stores. A key never ends in the
 * lowest digit, which is what guarantees a midpoint always exists between two
 * distinct keys — that is what lets the sidebar drop an item anywhere without
 * renumbering its neighbours.
 *
 * The client needs this to reorder stars (`stars.update` takes an index string
 * rather than a position), and the server has its own copy for its own writes.
 */

export const INDEX_ALPHABET = "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz";

const RADIX = INDEX_ALPHABET.length;
const MIDDLE = "P";

export function isValidIndex(value: unknown): value is string {
  if (typeof value !== "string" || value === "") return false;
  for (const character of value) {
    if (!INDEX_ALPHABET.includes(character)) return false;
  }
  return !value.endsWith(INDEX_ALPHABET.charAt(0));
}

function digitAt(key: string, position: number): number | null {
  if (position >= key.length) return null;
  const found = INDEX_ALPHABET.indexOf(key.charAt(position));
  return found < 0 ? null : found;
}

/** A key strictly between the two bounds; `null` on a side means unbounded. */
export function between(before: string | null, after: string | null): string {
  const lower = before !== null && isValidIndex(before) ? before : null;
  const upper = after !== null && isValidIndex(after) ? after : null;
  if (lower !== null && upper !== null && lower >= upper) return lower + MIDDLE;

  let prefix = "";
  for (let position = 0; ; position += 1) {
    const low = lower === null ? 0 : (digitAt(lower, position) ?? 0);
    const high = upper === null ? RADIX : (digitAt(upper, position) ?? 0);

    if (
      (lower === null || position >= lower.length) &&
      (upper === null || position >= upper.length)
    )
      return prefix + MIDDLE;
    if (high - low > 1) return prefix + INDEX_ALPHABET.charAt(Math.floor((low + high) / 2));
    prefix += INDEX_ALPHABET.charAt(low);
  }
}

/**
 * The key that places an item at `position` in a list whose members already
 * carry `keys`, in their rendered order.
 */
export function indexForPosition(keys: readonly (string | null)[], position: number): string {
  const clamped = Math.max(0, Math.min(position, keys.length));
  const before = clamped === 0 ? null : (keys[clamped - 1] ?? null);
  const after = clamped >= keys.length ? null : (keys[clamped] ?? null);
  return between(before, after);
}
